import { IonInput, IonRow, IonSpinner, IonText } from '@ionic/react';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCrateState,
  updateTrackField,
} from '../../store/slices/crate.slice';
import { AppDispatch } from '../../store/store';
import { iTrack } from '../../types/ITrack';
import { useToast } from '../ToastManager/useToast';
interface EditableNumberFieldProps {
  trackId: string;
  value: number | undefined;
  field: keyof iTrack;
  className?: string;
  textColor?: string;
}

export const EditableNumberField: React.FC<EditableNumberFieldProps> = ({
  trackId,
  value,
  field,
  className = '',
  textColor = 'dark',
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isEditable } = useSelector(selectCrateState);
  const [displayValue, setDisplayValue] = useState<string>(
    value?.toString() || '',
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const inputRef = useRef<HTMLIonInputElement>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const { errorToast } = useToast();

  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(value?.toString() || '');
      setHasUnsavedChanges(false);
    }
  }, [value, isFocused]);

  const saveChanges = async () => {
    if (!hasUnsavedChanges) return;

    let numValue: number | undefined;

    if (displayValue.trim() === '') {
      numValue = undefined;
    } else {
      const parsedValue = parseFloat(displayValue);
      if (isNaN(parsedValue) || parsedValue < 0 || parsedValue > 100) {
        errorToast({
          message: 'Please enter a number between 0 and 100',
        });
        setDisplayValue(value?.toString() || '');
        setHasUnsavedChanges(false);
        return;
      }
      numValue = parsedValue;
    }
    setIsUpdating(true);
    try {
      const result = await dispatch(
        updateTrackField({ trackId, field, value: numValue }),
      );

      if (updateTrackField.fulfilled.match(result)) {
        setHasUnsavedChanges(false);
      } else {
        setDisplayValue(value?.toString() || '');
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      setDisplayValue(value?.toString() || '');
      setHasUnsavedChanges(false);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleInput = (e: CustomEvent<{ value: string | null }>) => {
    const newValue = e.detail.value ?? '';
    setDisplayValue(newValue);
    const originalValue = value?.toString() || '';
    if (newValue !== originalValue) {
      setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(false);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = async () => {
    setIsFocused(false);
    await saveChanges();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      saveChanges();
      inputRef.current?.blur();
    }
    if (e.key === 'Escape') {
      setDisplayValue(value?.toString() || '');
      setHasUnsavedChanges(false);
      inputRef.current?.blur();
    }
  };

  return (
    <IonRow
      className={className}
      style={{ width: '100%', position: 'relative' }}
    >
      {isEditable ? (
        <div style={{ position: 'relative' }}>
          <IonInput
            ref={inputRef}
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            value={displayValue}
            onIonInput={(e) =>
              handleInput(e as CustomEvent<{ value: string | null }>)
            }
            onIonFocus={handleFocus}
            onIonBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className={className}
            color={textColor}
            disabled={isUpdating}
            style={{
              textAlign: 'center',
              minWidth: '3ch',
              maxWidth: '6ch',
              MozAppearance: 'textfield',
              WebkitAppearance: 'none',
              border: hasUnsavedChanges
                ? '2px solid var(--ion-color-primary)'
                : '1px solid transparent',
              borderRadius: '4px',
              opacity: isUpdating ? 0.6 : 1,
            }}
          />
          {isUpdating && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                right: '55px',
                transform: 'translateY(-50%)',
              }}
            >
              <IonSpinner
                name="crescent"
                style={{ width: '16px', height: '16px' }}
              />
            </div>
          )}
        </div>
      ) : (
        <IonText color={textColor}>
          <p style={{ textAlign: 'center' }}>{value ?? '--'}</p>
        </IonText>
      )}
    </IonRow>
  );
};
